import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { leadSources as initialSources, priorityRules as initialPriority } from '../data/configuration';
import { incomingLeads, leads as initialLeads } from '../data/leads';
import type { Activity, Lead, LeadSource, PriorityRule } from '../types';
import { requiredCallsFor } from '../utils/format';
export const CURRENT_OFFICER_ID = 'o-sarah';
interface AppStateValue {
  /** Published configuration — this is what drives the live LO queue. */
  publishedSources: LeadSource[];
  publishedPriority: PriorityRule[];
  /** Draft configuration being edited by management. */
  draftSources: LeadSource[];
  draftPriority: PriorityRule[];
  setDraftSources: React.Dispatch<React.SetStateAction<LeadSource[]>>;
  setDraftPriority: React.Dispatch<React.SetStateAction<PriorityRule[]>>;
  hasDraftChanges: boolean;
  lastPublished: string;
  publishConfiguration: () => void;
  discardDraft: () => void;
  allLeads: Lead[];
  queue: Lead[];
  /** Leads the LO has advanced past today — they sort behind untouched leads. */
  deferredIds: string[];
  currentLead: Lead | null;
  setCurrentLeadId: (id: string) => void;
  /** Leads with at least one activity dispositioned in this session. */
  activityLoggedIds: string[];
  /** Next Lead is gated until the LO logs an activity on the current lead. */
  canAdvance: boolean;
  nextLead: () => void;
  logCall: (leadId: string, outcome: 'Connected' | 'No Answer' | 'Voicemail') => void;
  logSms: (leadId: string) => void;
  logEmail: (leadId: string, templateName: string) => void;
  leadsWorkedToday: number;
  callsCompletedToday: number;
}
const AppStateContext = createContext<AppStateValue | null>(null);
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
export function AppStateProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [publishedSources, setPublishedSources] = useState<LeadSource[]>(() => clone(initialSources));
  const [publishedPriority, setPublishedPriority] = useState<PriorityRule[]>(() => clone(initialPriority));
  const [draftSources, setDraftSources] = useState<LeadSource[]>(() => clone(initialSources));
  const [draftPriority, setDraftPriority] = useState<PriorityRule[]>(() => clone(initialPriority));
  const [lastPublished, setLastPublished] = useState('Today at 7:42 AM by Dana Whitfield');
  const [allLeads, setAllLeads] = useState<Lead[]>(() => clone(initialLeads));
  const [currentLeadId, setCurrentLeadIdState] = useState<string | null>(null);
  const [deferredIds, setDeferredIds] = useState<string[]>([]);
  const [activityLoggedIds, setActivityLoggedIds] = useState<string[]>([]);
  const [leadsWorkedToday, setLeadsWorkedToday] = useState(12);
  const [callsCompletedToday, setCallsCompletedToday] = useState(27);
  const arrivals = useRef(0);
  const hasDraftChanges = useMemo(() => JSON.stringify(draftSources) !== JSON.stringify(publishedSources) || JSON.stringify(draftPriority) !== JSON.stringify(publishedPriority), [draftSources, draftPriority, publishedSources, publishedPriority]);
  const publishConfiguration = useCallback(() => {
    setPublishedSources(clone(draftSources));
    setPublishedPriority(clone(draftPriority));
    setLastPublished('Just now by you');
    toast.success('Configuration published', {
      description: 'Loan Officer queues are rebuilding with the new rules.'
    });
  }, [draftSources, draftPriority]);
  const discardDraft = useCallback(() => {
    setDraftSources(clone(publishedSources));
    setDraftPriority(clone(publishedPriority));
    toast('Draft changes discarded');
  }, [publishedSources, publishedPriority]);
  const queue = useMemo(() => {
    const order = new Map<string, number>();
    publishedPriority.filter(r => r.enabled).forEach((r, i) => order.set(r.id, i));
    return allLeads.filter(lead => lead.ownerId === CURRENT_OFFICER_ID).filter(lead => order.has(lead.priorityRuleId)).filter(lead => {
      const source = publishedSources.find(s => s.id === lead.sourceId);
      if (!source || !source.enabled) return false;
      return lead.callsToday < requiredCallsFor(source, lead.ageMinutes);
    }).sort((a, b) => {
      // Leads already presented today sit behind untouched leads, in the order
      // they were advanced past — so advancing never re-surfaces the same lead.
      const ra = deferredIds.indexOf(a.id);
      const rb = deferredIds.indexOf(b.id);
      if (ra !== rb) {
        if (ra === -1) return -1;
        if (rb === -1) return 1;
        return ra - rb;
      }
      const pa = order.get(a.priorityRuleId) ?? 99;
      const pb = order.get(b.priorityRuleId) ?? 99;
      if (pa !== pb) return pa - pb;
      if (a.callsToday !== b.callsToday) return a.callsToday - b.callsToday;
      return a.ageMinutes - b.ageMinutes;
    });
  }, [allLeads, publishedPriority, publishedSources, deferredIds]);
  useEffect(() => {
    if (!currentLeadId || !queue.some(l => l.id === currentLeadId)) {
      setCurrentLeadIdState(queue[0]?.id ?? null);
    }
  }, [queue, currentLeadId]);
  const currentLead = useMemo(() => queue.find(l => l.id === currentLeadId) ?? queue[0] ?? null, [queue, currentLeadId]);
  const canAdvance = currentLead ? activityLoggedIds.includes(currentLead.id) : false;
  const nextLead = useCallback(() => {
    if (!currentLead || !activityLoggedIds.includes(currentLead.id)) return;
    const upcoming = queue.filter(l => l.id !== currentLead.id);
    setDeferredIds(prev => [...prev.filter(id => id !== currentLead.id), currentLead.id]);
    setCurrentLeadIdState(upcoming[0]?.id ?? currentLead.id);
  }, [queue, currentLead, activityLoggedIds]);
  const addActivity = useCallback((leadId: string, activity: Activity, patch: Partial<Lead>) => {
    setAllLeads(prev => prev.map(lead => lead.id === leadId ? {
      ...lead,
      ...patch,
      activities: [activity, ...lead.activities]
    } : lead));
    setActivityLoggedIds(prev => prev.includes(leadId) ? prev : [...prev, leadId]);
  }, []);
  const logCall = useCallback((leadId: string, outcome: 'Connected' | 'No Answer' | 'Voicemail') => {
    const lead = allLeads.find(l => l.id === leadId);
    if (!lead) return;
    const source = publishedSources.find(s => s.id === lead.sourceId);
    const required = requiredCallsFor(source, lead.ageMinutes);
    const nextCount = lead.callsToday + 1;
    addActivity(leadId, {
      id: `a-${leadId}-${Date.now()}`,
      type: 'call',
      title: `Outbound call — ${outcome.toLowerCase()}`,
      minutesAgo: 0,
      outcome
    }, {
      callsToday: nextCount,
      lastContactMinutes: 0,
      contacted: outcome === 'Connected' ? true : lead.contacted,
      status: outcome === 'Connected' ? 'Contacted' : 'Attempting Contact'
    });
    setCallsCompletedToday(n => n + 1);
    if (nextCount >= required) {
      setLeadsWorkedToday(n => n + 1);
      toast.success(`${lead.name} completed today's cadence`, {
        description: `${required} of ${required} required calls logged — removed from the active queue.`
      });
    }
  }, [allLeads, publishedSources, addActivity]);
  const logSms = useCallback((leadId: string) => {
    addActivity(leadId, {
      id: `a-${leadId}-sms-${Date.now()}`,
      type: 'sms',
      title: 'SMS sent',
      detail: 'Sent from the work queue by the assigned Loan Officer.',
      minutesAgo: 0
    }, {
      lastContactMinutes: 0
    });
    toast.success('SMS sent');
  }, [addActivity]);
  const logEmail = useCallback((leadId: string, templateName: string) => {
    addActivity(leadId, {
      id: `a-${leadId}-email-${Date.now()}`,
      type: 'email',
      title: `Email sent — ${templateName}`,
      minutesAgo: 0
    }, {
      lastContactMinutes: 0
    });
    toast.success('Email sent', {
      description: `Template: ${templateName}`
    });
  }, [addActivity]);

  // Live queue: new leads route into the queue without a page refresh.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (arrivals.current >= incomingLeads.length) return;
      const lead = incomingLeads[arrivals.current];
      arrivals.current += 1;
      setAllLeads(prev => prev.some(l => l.id === lead.id) ? prev : [...prev, clone(lead)]);
      toast('New lead added below your current lead', {
        description: `${lead.name} — under 5 minutes old. Your current lead stays locked in place.`
      });
    }, 32000);
    return () => window.clearInterval(timer);
  }, []);
  const value: AppStateValue = {
    publishedSources,
    publishedPriority,
    draftSources,
    draftPriority,
    setDraftSources,
    setDraftPriority,
    hasDraftChanges,
    lastPublished,
    publishConfiguration,
    discardDraft,
    allLeads,
    queue,
    deferredIds,
    currentLead,
    setCurrentLeadId: setCurrentLeadIdState,
    activityLoggedIds,
    canAdvance,
    nextLead,
    logCall,
    logSms,
    logEmail,
    leadsWorkedToday,
    callsCompletedToday
  };
  return <AppStateContext.Provider value={value} data-id="element-400" data-mp="2h4op8e8cukut">{children}</AppStateContext.Provider>;
}
export function useAppState(): AppStateValue {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppState must be used inside AppStateProvider');
  return ctx;
}